import { GlobalStyles } from "@mui/material";
import {
  StyledEngineProvider,
  ThemeProvider as StyledThemeProvider,
} from "@mui/material/styles";
import { MutableRefObject, useEffect, useRef, useState } from "react";
import {
  HashRouter,
  Route,
  Switch,
  useHistory,
  useLocation,
} from "react-router-dom";
import styled from "styled-components";

import { ConfirmProvider } from "material-ui-confirm";
import { IntlProvider } from "react-intl";
import { Provider } from "react-redux";

import {
  Box,
  createTheme,
  CssBaseline,
  Grid,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";

import { useTranslation } from "common/locales";
import GenericDropdown from "features/general/GenericDropdown";
import {
  closePushNotificationModal,
  useAppDispatch,
  useAppSelector,
} from "model";
import {
  selectAccessibleFarmerDashboardsByAccessibleUserId,
  selectAllAccessibleFarmerDashboards,
} from "model/accessibleFarmerDashboardsSlice";
import { selectAllFarmerProgrammeDashboards } from "model/farmerDashboardsSlice";
import { selectAllProjectDashboards } from "model/projectDashboardsSlice";
import store, { RootState } from "model/rootReducer";
import { setSelectedAccessibleFarmer } from "model/selectedAccessibleFarmer";
import { setSelectedAccessibleFarmerDashboard } from "model/selectedAccessibleFarmerDashboard";
import { setSelectedFarmerDashboard } from "model/selectedFarmerDashboard";
import { setSelectedProjectDashboard } from "model/selectedProjectDashboard";
import { useSelector } from "react-redux";

import {
  BottomTabNavigation,
  PageTitleBar,
  PushNotificationModal,
} from "common/components";
import localeData from "common/locales";

import { useTokenRefresh } from "common/utils";
import NotificationProvider from "./NotificationProvider";
import RefreshProvider from "./RefreshProvider";

import { PrivateRoute } from "app";
import { MapView } from "features/mapLayers";
import { NewSignupDialog } from "features/signup";
import { MapRef } from "react-map-gl";
import Drawer from "./Drawer";
import LoginDialog from "./LoginDialog";

import { App as CapacitorApp } from "@capacitor/app";
import "@mui/material/styles/createPalette";
import AnimalGroupDetail from "features/animalGroupDetail/AnimalGroupDetail";
import AnimalGroupInput from "features/animalGroupDetail/AnimalGroupInput";
import { Covers } from "features/bulkCoverInput";
import { CombinedInputsOutputsDetail } from "features/combinedInputsOutputs";
import { FarmView } from "features/farmOverview";
import { FarmInput } from "features/farmOverview/FarmInput";
import FieldsListScreen from "features/farmOverview/FieldsListScreen";
import { FieldInput, FieldView } from "features/fieldDetail";
import FileManagement from "features/fileManagement/FileManagement";
import { AccessibleFarmsInsights } from "features/insights/AccessibleFarmsInsights";
import { FarmInsights } from "features/insights/FarmInsights";
import { ProjectInsights } from "features/insights/ProjectInsights";
import ProgrammeDetailsWeb from "features/programmesActions/ProgrammeDetailsWeb";
import Programmes from "features/programmesActions/Programmes";
import ProgrammesAction from "features/programmesActions/ProgrammesActions";

import ProgrammesList from "features/programmesActions/ProgrammesList";
import Oauth2Screen from "features/signup/Oauth2RedirectScreen";
import SignupIntroDialog from "features/signup/SignupIntroDialog";
import { drawerWidth, secondaryPanelWidth } from "width";
import PasswordForgotDialog from "./PasswordForgotDialog";
import PasswordResetDialog from "./PasswordResetDialog";

declare module "@mui/material/styles/createPalette" {
  // adding custom color to the theme
  interface Palette {
    carbon: PaletteColor;
    drawTool: PaletteColor;
  }
  interface PaletteOptions {
    carbon: PaletteColorOptions;
    drawTool: PaletteColorOptions;
  }
}

declare module "@mui/material" {
  interface ButtonPropsColorOverrides {
    carbon: true;
    drawTool: true;
  }

  interface ButtonGroupPropsColorOverrides {
    carbon: true;
    drawTool: true;
  }

  interface SvgIconPropsColorOverrides {
    carbon: true;
    drawTool: true;
  }
}

const spacing = (value: number) => `${8 * value}px`;

const secondaryDrawerWidthInPercent = 0.25;

const { palette } = createTheme();
const { augmentColor } = palette;

const createColor = (mainColor: string) =>
  augmentColor({ color: { main: mainColor } });

const theme = createTheme({
  spacing,
  components: {
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          fontSize: "1em",
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          fontSize: "0.875rem",
          lineHeight: 1.43,
          letterSpacing: "0.01071em",
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: `${spacing(1.5)} ${spacing(2.5)}!important`,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          // Standardized icon button colors - matches drawer navigation style
          // Dark grey by default, black on hover/selected
          color: "#616161", // Dark grey (Material Design grey 700)
          transition: "all 0.2s ease-in-out",

          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
            color: "#000000", // Black on hover (matches drawer)
          },

          "&:active": {
            backgroundColor: "rgba(0, 0, 0, 0.08)",
            color: "#000000", // Black on active
          },

          "&.Mui-selected": {
            color: "#000000", // Black when selected
          },

          "&:disabled": {
            color: "rgba(0, 0, 0, 0.26)",
            backgroundColor: "transparent",
          },

          // Primary color variant (when color="primary")
          "&.MuiIconButton-colorPrimary": {
            color: "#1C4321",

            "&:hover": {
              backgroundColor: "rgba(28, 67, 33, 0.08)",
              color: "#1C4321",
            },

            "&:active": {
              backgroundColor: "rgba(28, 67, 33, 0.16)",
              color: "#1C4321",
            },
          },

          // Secondary color variant (when color="secondary")
          "&.MuiIconButton-colorSecondary": {
            color: "#9E9E9E",

            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)",
              color: "#000000",
            },

            "&:active": {
              backgroundColor: "rgba(0, 0, 0, 0.08)",
              color: "#000000",
            },
          },
        },
      },
    },
    MuiSvgIcon: {
      styleOverrides: {
        root: {
          // Default icon color - dark grey when not in interactive context
          // This only applies when htmlColor is not explicitly set
          color: "#616161", // Dark grey (Material Design grey 700)
          transition: "color 0.2s ease-in-out",
          // When htmlColor is set via prop, don't apply default color
          // The htmlColor prop will be applied directly to the SVG element
          // and should override CSS color property
          "&[htmlcolor]": {
            color: "transparent",
          },
        },
      },
    },
  },
  palette: {
    mode: "light",
    primary: {
      main: "#1C4321",
    },
    secondary: {
      main: "#f50057",
    },
    error: {
      main: "#CD0303",
    },
    carbon: {
      main: "#7935bc",
    },
    drawTool: createColor("#e0e0e0"), // old main color
  },
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1280,
      xl: 1920,
    },
  },
});

// FarmViewPanel component to render FarmView to the right of drawer on desktop
function FarmViewPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FarmView is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
      }}
    >
      <FarmView />
    </Box>
  );
}

// FarmInputPanel component to render FarmInput (add farm form) to the right of drawer on desktop
function FarmInputPanel({
  mapRef,
}: {
  mapRef: MutableRefObject<MapRef | null>;
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FarmInput is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <FarmInput mapRef={mapRef} />
    </Box>
  );
}

// FieldInputPanel component to render FieldInput (add field form) to the right of drawer on desktop
function FieldInputPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FieldInput is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <FieldInput />
    </Box>
  );
}

// EditFieldInputPanel component to render FieldInput (edit field form) to the right of drawer on desktop
function EditFieldInputPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FieldInput is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <FieldInput />
    </Box>
  );
}

// FieldViewPanel component to render FieldView to the right of drawer on desktop
function FieldViewPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FieldView is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <FieldView />
    </Box>
  );
}

// FieldViewPanel component to render FieldView to the right of drawer on desktop
function FileManagementPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FieldView is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <FileManagement />
    </Box>
  );
}

// AnimalGroupInputPanel component to render AnimalGroupInput (add animal group form) to the right of drawer on desktop
function AnimalGroupInputPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - AnimalGroupInput is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <AnimalGroupInput />
    </Box>
  );
}

// EditAnimalGroupInputPanel component to render AnimalGroupInput (edit animal group form) to the right of drawer on desktop
function EditAnimalGroupInputPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - AnimalGroupInput is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <AnimalGroupInput />
    </Box>
  );
}

// AnimalGroupDetailPanel component to render AnimalGroupDetail to the right of drawer on desktop
function AnimalGroupDetailPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - AnimalGroupDetail is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <AnimalGroupDetail />
    </Box>
  );
}

// FieldsListScreenPanel component to render FieldsListScreen to the right of drawer on desktop
function FieldsListScreenPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - FieldsListScreen is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        width: `${secondaryPanelWidth}px`,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
      }}
    >
      <FieldsListScreen />
    </Box>
  );
}

// CoversPanel component to render Covers to the right of drawer on desktop
function CoversPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - Covers is shown in drawer
  if (isMobile) {
    return null;
  }

  return <Covers />;
}

// CombinedInputsOutputsDetailPanel component to render CombinedInputsOutputsDetail to the right of drawer on desktop
function CombinedInputsOutputsDetailPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - CombinedInputsOutputsDetail is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <CombinedInputsOutputsDetail />
    </Box>
  );
}

// ProgrammesPanel component to render ProgrammesList (which includes questionnaire iframe) on desktop
function ProgrammesPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render - ProgrammesDrawer is shown in drawer
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <ProgrammesList />
    </Box>
  );
}

// ProgrammeDetailsPanel component to render ProgrammeDetailsWeb to the right of drawer on desktop
function ProgrammeDetailsPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  // On mobile, don't render - ProgrammeDetailsDrawer is shown in drawer
  if (isMobile) {
    return null;
  }

  // When questionnaire is started, don't render this panel - iframe takes full width
  if (questionnaireStarted.questionnaireStarted) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "white",
      }}
    >
      <ProgrammeDetailsWeb />
    </Box>
  );
}

// FarmInsightsContentPanel component to render stripe with selectors and FarmInsights below on desktop
function FarmInsightsContentPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const history = useHistory();

  const farmerDashboards = useAppSelector(selectAllFarmerProgrammeDashboards);
  const selectedDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedFarmerDashboard
  );

  // On mobile, don't render
  if (isMobile) {
    return null;
  }

  const farmerDashboardsAsMap: { [key: string]: string } = {};
  farmerDashboards.forEach((dashboard) => {
    farmerDashboardsAsMap[dashboard.guid] = dashboard.title;
  });

  if (!selectedDashboard.guid && farmerDashboards.length > 0) {
    const firstDashboard = farmerDashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedFarmerDashboard({
          guid: firstDashboard.guid,
          title: firstDashboard.title,
        })
      );
    }
  }

  // If no farm dashboards, show clickable message
  if (farmerDashboards.length === 0) {
    return (
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: `${drawerWidth}px`,
          right: 0,
          bottom: 0,
          height: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1300,
          backgroundColor: "white",
        }}
      >
        <Box
          sx={{
            textAlign: "center",
            p: 4,
          }}
        >
          <Typography
            variant="body1"
            color="text.secondary"
            onClick={() => history.push("/programmes")}
            sx={{
              cursor: "pointer",
              textDecoration: "underline",
              "&:hover": {
                color: "primary.main",
              },
            }}
          >
            Sign up to a programme to gain more insights into your farm!
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`, // Right after drawer
        right: 0,
        bottom: 0,
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        zIndex: 1300,
        backgroundColor: "transparent",
      }}
    >
      {/* Title bar */}
      <PageTitleBar title={t("go.to.farm.insights")} showDivider={true} />
      <Box
        sx={{
          backgroundColor: "white",
          padding: 2,
          borderBottom: "1px solid #e0e0e0",
          display: "flex",
          gap: 2,
          alignItems: "center",
        }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <GenericDropdown
              required={false}
              disabled={false}
              flipKeyValue={true}
              onChange={(event) => {
                const selectedDashboard = farmerDashboards.filter(
                  (d) => d.guid === event.target.value
                )[0];

                dispatch(setSelectedFarmerDashboard(selectedDashboard));
              }}
              currentValue={selectedDashboard.guid}
              label={"farmer.dashboard.label"}
              name={`selectedFarmerDashboard`}
              possibleValues={farmerDashboardsAsMap}
              tPrefix={"reseedEvent.totalAmountUnit.label"}
              inputError={false}
              shouldTranslate={false}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Content below stripe */}
      <Box sx={{ flex: 1, overflow: "auto" }}>
        <FarmInsights />
      </Box>
    </Box>
  );
}

// AccessibleFarmsInsightsContentPanel component to render stripe with selectors and AccessibleFarmsInsights below on desktop
function AccessibleFarmsInsightsContentPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const selectedAccessibleFarmer = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedAccessibleFarmer
  );
  const selectedAccessibleFarmerDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedAccessibleFarmerDashboard
  );
  const accessibleFarmerDashboards = useAppSelector((state) =>
    selectAccessibleFarmerDashboardsByAccessibleUserId(
      state,
      selectedAccessibleFarmer?.guid ?? ""
    )
  );
  const allAccessibleFarmerDashboards = useAppSelector((state) =>
    selectAllAccessibleFarmerDashboards(state)
  );

  // On mobile, don't render
  if (isMobile) {
    return null;
  }

  const disabledFarmerList: { [key: string]: string } = {};
  for (const userDashboards of allAccessibleFarmerDashboards) {
    if (userDashboards.dashboards.length === 0) {
      disabledFarmerList[userDashboards.guid] = "no accessible dashboard";
    }
  }

  const farmerDashboardsAsMap: { [key: string]: string } = {};
  accessibleFarmerDashboards?.dashboards.forEach((dashboard) => {
    farmerDashboardsAsMap[dashboard.visualizationId] = dashboard.title;
  });

  const userNamesAsMap: { [key: string]: string } = {};
  allAccessibleFarmerDashboards.forEach((userDashboards) => {
    userNamesAsMap[userDashboards.guid] = userDashboards.name;
  });

  if (
    !selectedAccessibleFarmer.guid &&
    allAccessibleFarmerDashboards.length > 0
  ) {
    for (const userDashboards of allAccessibleFarmerDashboards) {
      if (userDashboards.dashboards.length > 0) {
        const firstDashboard = userDashboards.dashboards[0];
        if (firstDashboard) {
          dispatch(setSelectedAccessibleFarmer(userDashboards.guid));
          break;
        }
      }
    }
  }

  if (
    !selectedAccessibleFarmerDashboard.guid &&
    accessibleFarmerDashboards?.dashboards &&
    (accessibleFarmerDashboards.dashboards.length ?? -1) > 0
  ) {
    const firstDashboard = accessibleFarmerDashboards.dashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedAccessibleFarmerDashboard({
          guid: firstDashboard.visualizationId,
          title: firstDashboard.title,
        })
      );
    }
  } else if (
    selectedAccessibleFarmerDashboard.guid &&
    accessibleFarmerDashboards?.dashboards &&
    (accessibleFarmerDashboards.dashboards.length ?? -1) === 0
  ) {
    dispatch(
      setSelectedAccessibleFarmerDashboard({
        guid: null,
        title: null,
      })
    );
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: `${drawerWidth}px`, // Right after drawer
          right: 0,
          bottom: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          zIndex: 1300,
          backgroundColor: "transparent",
        }}
      >
        <PageTitleBar title={t("go.to.farm.insights")} showDivider />
        {/* Stripe with selectors */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <GenericDropdown
                required={false}
                disabled={false}
                flipKeyValue={true}
                onChange={(event) => {
                  const selectedUser = allAccessibleFarmerDashboards.filter(
                    (d) => d.guid === event.target.value
                  )[0];
                  dispatch(setSelectedAccessibleFarmer(selectedUser.guid));
                }}
                currentValue={selectedAccessibleFarmer.guid}
                label={"farmer.dashboard.farmer.selection.label"}
                name={`selectedFarmerDashboard`}
                possibleValues={userNamesAsMap}
                tPrefix={"reseedEvent.totalAmountUnit.label"}
                inputError={false}
                shouldTranslate={false}
                itemDisabled={disabledFarmerList}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <GenericDropdown
                required={false}
                disabled={
                  (accessibleFarmerDashboards?.dashboards?.length ?? 0) < 1
                }
                flipKeyValue={true}
                onChange={(event) => {
                  const selectedDashboard =
                    accessibleFarmerDashboards?.dashboards.filter(
                      (d) => d.visualizationId === event.target.value
                    )[0];

                  dispatch(
                    setSelectedAccessibleFarmerDashboard({
                      guid: selectedDashboard?.visualizationId,
                      title: selectedDashboard?.title,
                    })
                  );
                }}
                currentValue={selectedAccessibleFarmerDashboard.guid}
                label={"farmer.dashboard.dashboard.selection.label"}
                name={`selectedFarmerDashboard`}
                possibleValues={farmerDashboardsAsMap}
                tPrefix={"reseedEvent.totalAmountUnit.label"}
                inputError={false}
                shouldTranslate={false}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Content below stripe */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <AccessibleFarmsInsights />
        </Box>
      </Box>
    </>
  );
}

// ProjectInsightsContentPanel component to render stripe with selectors and ProjectInsights below on desktop
function ProjectInsightsContentPanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const projectDashboards = useAppSelector(selectAllProjectDashboards);
  const selectedDashboard = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).selectedProjectDashboard
  );

  // On mobile, don't render
  if (isMobile) {
    return null;
  }

  const projectDashboardsAsMap: { [key: string]: string } = {};
  projectDashboards.forEach((dashboard) => {
    projectDashboardsAsMap[dashboard.guid] = dashboard.title;
  });

  if (!selectedDashboard.guid && projectDashboards.length > 0) {
    const firstDashboard = projectDashboards[0];
    if (firstDashboard) {
      dispatch(
        setSelectedProjectDashboard({
          guid: firstDashboard.guid,
          title: firstDashboard.title,
        })
      );
    }
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: `${drawerWidth}px`, // Right after drawer
          right: 0,
          bottom: 0,
          height: "100vh",
          display: "flex",
          flexDirection: "column",
          zIndex: 1300,
          backgroundColor: "transparent",
        }}
      >
        <PageTitleBar title={t("toolbar.projectinsighs.title")} showDivider />
        {/* Stripe with selectors */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: 2,
            borderBottom: "1px solid #e0e0e0",
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <GenericDropdown
                required={false}
                disabled={projectDashboards.length < 1}
                flipKeyValue={true}
                onChange={(event) => {
                  dispatch(
                    setSelectedProjectDashboard(
                      projectDashboards.filter(
                        (d) => d.guid === event.target.value
                      )[0]
                    )
                  );
                }}
                currentValue={selectedDashboard.guid}
                label={"projects.dashboard.label"}
                name={`selectedFarmerDashboard`}
                possibleValues={projectDashboardsAsMap}
                tPrefix={"reseedEvent.totalAmountUnit.label"}
                inputError={false}
                shouldTranslate={false}
              />
            </Grid>
          </Grid>
        </Box>

        {/* Content below stripe */}
        <Box sx={{ flex: 1, overflow: "auto" }}>
          <ProjectInsights />
        </Box>
      </Box>
    </>
  );
}

// ProgrammesQuestionnairePanel component to render Programmes to the right of ProgrammesDrawer on desktop
function ProgrammesQuestionnairePanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // On mobile, don't render
  if (isMobile) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth + secondaryPanelWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "transparent",
      }}
    >
      <Programmes />
    </Box>
  );
}

// ProgrammeDetailsQuestionnairePanel component to render ProgrammesAction to the right of drawer on desktop
function ProgrammeDetailsQuestionnairePanel() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const questionnaireStarted = useSelector(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return
    (state) => (state as any).questionnaireStarted
  );

  // On mobile, don't render
  if (isMobile) {
    return null;
  }

  // Only render the panel when questionnaire is actually started
  if (!questionnaireStarted.questionnaireStarted) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "fixed",
        top: 0,
        left: `${drawerWidth}px`,
        right: 0,
        bottom: 0,
        height: "100vh",
        overflow: "auto",
        zIndex: 1300,
        backgroundColor: "transparent",
      }}
    >
      <ProgrammesAction />
    </Box>
  );
}

// New AppContent component to handle routing and back button
function AppContent() {
  const mapRef = useRef<MapRef>(null);
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isLoggedIn = useAppSelector((state: RootState) => state.app.loggedIn);
  const currentFarmId = useAppSelector(
    (state: RootState) => state.farms.currentFarmId
  );

  const pushNotificationModal = useAppSelector(
    (state: RootState) => state.app.pushNotificationModal
  );

  useEffect(() => {
    const handleBackButton = async () => {
      // Check if there's an active touch event on the map
      const activeElement = document.activeElement;
      const isMapEvent = activeElement?.closest(
        '[data-source="map-container"]'
      );

      // Don't handle back button if it's a map interaction
      if (isMapEvent) {
        return;
      }

      try {
        if (
          !isLoggedIn &&
          (location.pathname === "/" || location.pathname === "/login")
        ) {
          // Only exit if user is not logged in and at root/login
          await CapacitorApp.exitApp();
        } else if (
          isLoggedIn &&
          location.pathname === "/initial-screen-select"
        ) {
          // If logged in and at farm view, exit app
          await CapacitorApp.exitApp();
        } else {
          // Otherwise, go back in history
          history.goBack();
        }
      } catch (error) {
        console.error("Error handling back button:", error);
      }
    };

    // Add back button listener for Android
    void CapacitorApp.addListener("backButton", handleBackButton);

    return () => {
      // Cleanup listener when component unmounts
      void CapacitorApp.removeAllListeners();
    };
  }, [history, location, isLoggedIn, currentFarmId]);

  return (
    <Root>
      <Switch>
        <Route path="/login" exact children={<LoginDialog />} />
        <Route path="/signup" children={<NewSignupDialog />} />
        <Route path="/signup-intro" children={<SignupIntroDialog />} />
        <PrivateRoute path="/oauth2" children={<Oauth2Screen />} />
        <Route
          path="/login/password-forgot"
          exact
          children={<PasswordForgotDialog />}
        />
        <Route
          path="/login/password-reset"
          exact
          children={<PasswordResetDialog />}
        />
        <PrivateRoute
          path="/farm-insights"
          children={<FarmInsightsContentPanel />}
        />
        <PrivateRoute
          path="/project-insights"
          children={<ProjectInsightsContentPanel />}
        />
        <PrivateRoute
          path="/accessible-farms-insights"
          children={<AccessibleFarmsInsightsContentPanel />}
        />

        <PrivateRoute
          path="/programme-details/:programmeId"
          children={
            <>
              <ProgrammeDetailsPanel />
              <ProgrammeDetailsQuestionnairePanel />
            </>
          }
        />

        <PrivateRoute path="/programmes" children={<ProgrammesPanel />} />

        <PrivateRoute
          path="/file-management-overview"
          children={<FileManagementPanel />}
        />

        <PrivateRoute path="/farm/:farmId" children={<FarmViewPanel />} />

        <PrivateRoute
          path="/add-farm"
          children={<FarmInputPanel mapRef={mapRef} />}
        />

        <PrivateRoute path="/add-field" children={<FieldInputPanel />} />

        <PrivateRoute
          path="/edit-field/:fieldId"
          children={<EditFieldInputPanel />}
        />

        <PrivateRoute path="/field/:fieldId" children={<FieldViewPanel />} />

        <PrivateRoute
          path="/add-animal-group"
          children={<AnimalGroupInputPanel />}
        />

        <PrivateRoute
          path="/edit-animal-group/:animalGroupId"
          children={<EditAnimalGroupInputPanel />}
        />

        <PrivateRoute
          path="/animal-group/:animalGroupId"
          children={<AnimalGroupDetailPanel />}
        />

        <PrivateRoute
          path="/fields-list/:fieldType"
          children={<FieldsListScreenPanel />}
        />

        <PrivateRoute
          path="/add-covers"
          children={
            <>
              <FarmViewPanel />
              <CoversPanel />
            </>
          }
        />

        <PrivateRoute
          path="/inputs-and-outputs"
          children={<CombinedInputsOutputsDetailPanel />}
        />

        <PrivateRoute path="/" exact children={<LoginDialog />} />
      </Switch>

      <>
        <Drawer mapRef={mapRef} />
        <MapView mapRef={mapRef} />
      </>

      {/* Bottom Tab Navigation for Mobile */}
      <BottomTabNavigation />

      {/* Global Push Notification Modal */}
      <PushNotificationModal
        open={pushNotificationModal.open}
        onClose={() => dispatch(closePushNotificationModal())}
        notificationData={pushNotificationModal.data}
      />
    </Root>
  );
}

export default function App() {
  let locale = store.getState().app.locale;
  const [messages, setMessages] = useState(localeData["en"]);
  useTokenRefresh();

  const updateLocale = () => {
    locale = store.getState().app.locale;
    if (Object.keys(localeData).includes(locale)) {
      setMessages(localeData[locale]);
    } else {
      setMessages(localeData["en"]);
    }
  };
  store.subscribe(updateLocale);

  return (
    <Provider store={store}>
      <IntlProvider locale={locale} key={locale} messages={messages}>
        <HashRouter>
          <GlobalStyles styles={{ "*": { "touch-action": "pan-x pan-y" } }} />
          <CssBaseline />
          <StyledEngineProvider injectFirst>
            <ThemeProvider theme={theme}>
              <StyledThemeProvider theme={theme}>
                <NotificationProvider>
                  <ConfirmProvider>
                    <RefreshProvider>
                      <AppContent />
                    </RefreshProvider>
                  </ConfirmProvider>
                </NotificationProvider>
              </StyledThemeProvider>
            </ThemeProvider>
          </StyledEngineProvider>
        </HashRouter>
      </IntlProvider>
    </Provider>
  );
}

const Root = styled.div`
  flex-grow: 1;
`;
